import { useCallback, useEffect, useState } from 'react';
import { BalanceReturn, CouponReturn } from '../clients/rust-api-client';
import { memberBillingService } from '../clients/ApiClients';
import { billingErrorMessage } from '../utils/billingHelpers';

interface AccountData {
  balance: BalanceReturn | null;
  coupon: CouponReturn | null;
  loading: boolean;
  error: string | null;
  clearError: () => void;
  setError: (message: string) => void;
}

/**
 * The balance and coupon for one home.
 *
 * The two load together because the hero needs a due date and the
 * balance does not carry one — `BalanceReturn` has
 * `oldest_unpaid_due_date`, which is the oldest *overdue* charge and
 * not the same thing as "when is the next payment due". The coupon has
 * the real `due_date` and `grace_date`.
 *
 * **A failed coupon is not a failed screen.** `StatementService::coupon`
 * returns a 400 when the HOA has no `hoa_code` yet, which is a
 * setup state rather than an error the homeowner caused — so the
 * balance still renders and only the "how to pay" card goes missing.
 * Failing both would leave someone unable to see what they owe because
 * their association has not finished onboarding.
 */
export const useAccount = (homeId?: string | null): AccountData => {
  const [balance, setBalance] = useState<BalanceReturn | null>(null);
  const [coupon, setCoupon] = useState<CouponReturn | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  useEffect(() => {
    if (!homeId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await memberBillingService.getMyBalance({ homeId });
        if (cancelled) return;
        setBalance(data);
      } catch (err) {
        if (cancelled) return;
        // A 404 here means "not one of your homes", which the backend
        // answers instead of 403 so ids cannot be probed. Render it as
        // not found, never as forbidden.
        setError(billingErrorMessage(err, "We couldn't load your balance."));
      }

      try {
        const { data } = await memberBillingService.getMyCoupon({ homeId });
        if (cancelled) return;
        setCoupon(data);
      } catch {
        if (cancelled) return;
        setCoupon(null);
      }

      if (!cancelled) setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [homeId]);

  return { balance, coupon, loading, error, clearError, setError };
};
